import dayjs from "dayjs";
import { BaseChargeFragment, BaseLocationFragment, CalendarEvent_LocationFragment, Charge, Job_CalendarEventFragment, JobDetailsV2Fragment, RouteDistances } from "graphql.generated";

import { cloneDeep , isNil } from "lodash";

import { PrimeIcons } from 'primeng/api';

import { EventLocationTypes, EventTypeInfo, eventTypeInfoMapV2, locationNameMap } from "../global.constants";
import { safeParseJSON } from "../js";
import { ProductMetadataValue } from "../services/product-rules.service";

import { UserModifiedTravelTime } from "./job-tool.reducer";

export type ChargeProductType = 'traveltime' | 'locationtime';

export type TimeFromConfig = {
    estimating: number | undefined;
    virtualEstimate: number | undefined;
};

export const LOCATION_ICON_MAP = {
    start: PrimeIcons.MAP_MARKER,
    end: PrimeIcons.FLAG,
    basestart: PrimeIcons.WAREHOUSE,
    baseend: PrimeIcons.WAREHOUSE,
}

export interface EventInfoLocationJobsV2 extends CalendarEvent_LocationFragment {
    fullType: string; // Eg. Moving-BaseStart, Packing-Start
    totalTravelTimeToNextLocation?: number;
    arrivalTimeEpoch?: number;
    departureTimeEpoch?: number;
    arrivalTime?: string;
    departureTime?: string;
    icon: string;
    name: string; // Eg: Dock, Start, End
}

export interface EventInfoJobsV2 {
    id?: string;
    eventType: string;
    eventTypeInfo: EventTypeInfo;
    locations: EventInfoLocationJobsV2[];
    totalTime: number;
    totalLocationTime: number;
    totalTravelTime: number;
    totalDistanceMeters: number;
    baseLocationTypes: string[];
    includeStartDock: boolean;
    includeEndDock: boolean;
  }

/**
   * Used to precalculate event information in selectors
   *
   * @param eventType the event type to calculate
   * @param locationTypeMap a map of location types to system location ID's
   * @returns an object with the event info
   */
export function precalculateEventInfo(
    calendarEvent: Job_CalendarEventFragment,
    job: JobDetailsV2Fragment,
    includeStartDock = true,
    includeEndDock = true,
): EventInfoJobsV2 {
    const { type: eventType, locations: eventLocations = [] } = calendarEvent;
    const eventTypeDetails = eventTypeInfoMapV2[eventType];
    const routeDistances = job?.distances || {};

    // Determine the base location types for the event
    const baseLocationTypes = eventTypeDetails?.virtualTime
        ? []
        : calculateBaseLocationTypes(
              includeStartDock,
              true,
              eventType === 'moving',
              includeEndDock,
          );

    const eventLocationDetails: EventInfoLocationJobsV2[] = [];

    let locationOrder = 0;
    for (const baseLocationType of baseLocationTypes) {
        const mappedLocationType = locationNameMap[baseLocationType];
        let matchingEventLocation = structuredClone(eventLocations?.find(
            (location) => location.type === baseLocationType,
        ));

        // Find the location in the job locations
        if (!matchingEventLocation) {
            const matchingJobLocation = job.locations.find(
                (location) => location.locationType === mappedLocationType,
            );

            matchingEventLocation = {
                id: null,
                order: null,
                type: baseLocationType,
                location: matchingJobLocation?.location,
            }
        }

        const fullLocationType = `${eventType}-${baseLocationType}`;
        const estimatedTimeAtLocation = matchingEventLocation?.estimatedTimeAtLocation || 0;

        let travelTimeToNextLocation = matchingEventLocation?.travelTimeToNextLocation || 0;
        let distanceToNextLocation = matchingEventLocation?.distanceToNextLocation || 0;

        const nextBaseLocationType = baseLocationTypes[locationOrder + 1];
        if (nextBaseLocationType) {
            const distanceDetails = getDistanceBetweenLocationTypes(
                baseLocationType,
                nextBaseLocationType,
                routeDistances,
            );
            if (distanceDetails) {
                travelTimeToNextLocation = distanceDetails.estimatedTime;
                distanceToNextLocation = distanceDetails.totalDistance;
            }
        }

        eventLocationDetails.push({
            ...matchingEventLocation,

            // Overwrite location distance/travel-times
            estimatedTimeAtLocation,
            travelTimeToNextLocation,
            distanceToNextLocation,
            travelTimeToNextLocationOffset: matchingEventLocation?.travelTimeToNextLocationOffset || 0,
            order: locationOrder++,

            // Details to display in booking dialog
            icon: LOCATION_ICON_MAP[baseLocationType],
            fullType: fullLocationType,
            name: mappedLocationType,
        });
    }

    const eventTotals = calculateEventInfoTotals(eventLocationDetails);

    // Add virtual time to total location time and total time
    eventTotals.totalLocationTime += eventTypeDetails?.virtualTime || 0;
    eventTotals.totalTime += eventTypeDetails?.virtualTime || 0;

    return {
        eventType,
        eventTypeInfo: eventTypeDetails,
        baseLocationTypes,
        locations: eventLocationDetails,
        includeStartDock,
        includeEndDock,
        ...eventTotals,
    };
}

export function  calculateBaseLocationTypes(
    includeStartDock = true,
    includeStart = true,
    includeEnd = true,
    includeEndDock = true,
) {
    const baseLocationTypes: EventLocationTypes[] = [];

    if (includeStartDock) {
      baseLocationTypes.push(EventLocationTypes.dockStart);
    }
    if (includeStart) {
      baseLocationTypes.push(EventLocationTypes.start);
    }
    if (includeEnd) {
      baseLocationTypes.push(EventLocationTypes.end);
    }
    if (includeEndDock) {
      baseLocationTypes.push(EventLocationTypes.dockEnd);
    }

    return baseLocationTypes;
}

export function calculateEventInfoTotals(locations: EventInfoLocationJobsV2[]) {

    let totalLocationTime = 0;
    let totalTravelTime = 0;
    let totalDistanceMeters = 0;

    for (const l of locations) {
        totalLocationTime += l.estimatedTimeAtLocation || 0;
        totalTravelTime += l.travelTimeToNextLocation || 0;
        totalTravelTime += l.travelTimeToNextLocationOffset || 0;
        totalDistanceMeters += l.distanceToNextLocation || 0;
    }
    return {
        totalTime: totalLocationTime + totalTravelTime,
        totalLocationTime,
        totalTravelTime,
        totalDistanceMeters,
    };

}

/**
   * Get distances between two event location types
   *
   * @param startEventType
   * @param endEventType
   * @param locationDistanceTypeMap
   */
export function getDistanceBetweenLocationTypes(
    startEventType: string,
    endEventType: string,
    distances: RouteDistances,

    locationDistanceTypeMap = {
        basestart: 'dock',
        baseend: 'dock',
        start: 'start',
        end: 'end',
    },
) {

    const from = locationDistanceTypeMap[startEventType] || startEventType;
    const to = locationDistanceTypeMap[endEventType] || endEventType;
    const distanceKey = `${from}_${to}`;
    const reversedDistanceKey = `${to}_${from}`;

    return distances[distanceKey] || distances[reversedDistanceKey];
}

export function getEventVirtualTime(
    eventTypeInfo: EventTypeInfo,
    eventTimeFromConfig: TimeFromConfig,
): number {
    let virtualTime = eventTypeInfo.virtualTime || 0;

    if (eventTypeInfo.value === 'virtualEstimate' && eventTimeFromConfig?.virtualEstimate) {
        virtualTime = eventTimeFromConfig.virtualEstimate;
    }

    return virtualTime;
}

/**
   * Update estimated time at each location and total time from charges.
   *
   * Includes time at location and estimated travel time
   *EventInfo
   * @param eventInfo Event info, probably calculated from `precalculateEventInfo`
   * @param eventCharges charges on a job to modify the estimated times based on changes in hours/travel
   */
export function getDurationsFromCharges(
    eventInfo: EventInfoJobsV2,
    eventCharges: BaseChargeFragment[],
    eventTimeFromConfig: TimeFromConfig,
) {
    const eventTypeInfo = eventTypeInfoMapV2[eventInfo.eventType];

    let updatedLocations = cloneDeep(eventInfo.locations);

    const determineCalculationForProduct = (
        productType: ChargeProductType,
        eventTime: number,
    ): {
        timeMultiplier: number;
        timeToAdd: number;
    } => {

        const {
            quantitySum: chargeTimeHours,
            charges: summedCharges,
        } = calculateChargesRelatedToEvent(eventCharges, productType);
        const chargeTime = chargeTimeHours * 3600;

        if (
            // charge time and event time are both zero
            chargeTime === 0 && eventTime === 0
            // there are no summed charges
            && !summedCharges?.length
            // we are calculating the location time
            && productType === 'locationtime'
            // the total time at each location is zero
            && sumTimeAtEventLocations(eventInfo.locations) === 0
            // event type has a default location time
            && eventTypeInfo?.defaultLocationTimes
        ) {
            // missing both rules and charges, set location time from constants
            updatedLocations  = [];
            for (const location of eventInfo.locations) {
                updatedLocations = [...updatedLocations, setDefaultTimeAtLocation(location, eventTypeInfo, eventTimeFromConfig)];
            }

            return {
                timeMultiplier: 1,
                timeToAdd: 0,
            };
        }
        if (summedCharges.length === 0) {
            // use rule time because no charges were added.
            // console.warn(`Not multiplying ${productType} for ${eventInfo.eventType}, no charges. Event time: ${eventTime}`);

            return {
                timeMultiplier: 1,
                timeToAdd: 0,
            };
        }

        let addTime = 0;
        let multiple = chargeTime / eventTime;
        if (chargeTime === 0 && eventTime === 0) {
            // houston we have a problem
            console.warn(`cant calculate ${eventInfo.eventType} ${productType} `
                + `duration from charges, charge time and event time are 0`);

            return {
                timeMultiplier: 1,
                timeToAdd: 0,
            };
        } else if (eventTime === 0) {
            // use charge time by default
            addTime = chargeTime;
            multiple = 1;
        }

        return {
            timeMultiplier: multiple,
            timeToAdd: addTime,
        };
    };

    const {
        timeMultiplier: locationTimeMultiple,
        timeToAdd: locationTimeAdd,
    } = determineCalculationForProduct('locationtime', eventInfo.totalLocationTime);
    const {
        timeMultiplier: travelTimeMultiple,
        timeToAdd: travelTimeAdd,
    } = determineCalculationForProduct('traveltime', eventInfo.totalTravelTime);

    const nonBaseLocations = updatedLocations.filter((l) => !isBaseLocation(l));

    updatedLocations = updatedLocations.map((location) => {
        const isNonBaseLocation = !isBaseLocation(location);

        const adjustedEstimatedTime = isNonBaseLocation
            ? Math.ceil(location.estimatedTimeAtLocation * locationTimeMultiple + locationTimeAdd / nonBaseLocations.length)
            : location.estimatedTimeAtLocation;

        const adjustedTravelTime = Math.ceil(
            location.travelTimeToNextLocation * travelTimeMultiple + travelTimeAdd / updatedLocations.length
        );
        const travelTimeToNextLocationOffset = adjustedTravelTime - location.travelTimeToNextLocation;

        return {
            ...location,
            // Calculate estimated time at location
            estimatedTimeAtLocation: adjustedEstimatedTime,
            // Calculate travel time to next location offset
            travelTimeToNextLocationOffset,
        };
    });

    const res = calculateEventInfoTotals(updatedLocations);

    const virtualTime = getEventVirtualTime(eventTypeInfo, eventTimeFromConfig);

    const updatedEventInfo = {
        ...eventInfo,
        locations: updatedLocations,
        totalTime: res.totalTime + virtualTime,
        totalLocationTime: res.totalLocationTime + virtualTime,
        totalTravelTime: res.totalTravelTime,
        totalDistanceMeters: res.totalDistanceMeters,
    };

    return updatedEventInfo;

}

export function calculateChargesRelatedToEvent(
    eventCharges: BaseChargeFragment[],
    productType: ChargeProductType,
) {

    const charges = [];
    let valueSum = 0;
    let quantitySum = 0;

    for (const charge of eventCharges) {
        const chargeProductType = determineChargeProductType(charge);

        if (chargeProductType === productType) {
            charges.push(charge);
            quantitySum += charge.quantity;
            valueSum += charge.amount;
        }

    }

    return {
        charges,
        quantitySum,
        valueSum,
    };
}

export function /**
* Determines charge product type
*
* @param charge
* @returns
*/
determineChargeProductType(
    charge: BaseChargeFragment,
): ChargeProductType {

    // Authomatic Charges/Rules
    const strAutoInfo = getAutoInfoFromCharge(charge as any);
    if (strAutoInfo) {
        const autoInfo = safeParseJSON<ProductMetadataValue>(strAutoInfo);
        if (!autoInfo) {
        } else if (autoInfo.quantity === 'event-total-traveltime') {
            return 'traveltime';
        } else if (autoInfo.quantity === 'event-total-locationtime') {
            return 'locationtime';
        }
    }

    // Custom Charges (w/out products) don't apply
    if (!charge.product?.category) {
        return undefined;
    }

    // Check the category first as this is the cleaner way
    switch (charge.product?.category) {
        case 'Truck & Travel':
            return 'traveltime';
        case 'Labor':
            return 'locationtime';
    }


    // Check the name if the category is not set, this way is deprecated and will be removed in the future
    const productName = (charge.product?.name || charge.productName)?.toLowerCase();

    if (charge.attributes?.includes('productType::traveltime')) {
        return 'traveltime';
    } else if (charge.attributes?.includes('productType::locationtime')) {
        return 'locationtime';
    } else if (charge.product?.attributes?.includes('productType::traveltime')) {
        return 'traveltime';
    } else if (charge.product?.attributes?.includes('productType::locationtime')) {
        return 'locationtime';
    } else if (productName.includes('man hour')) {
        return 'locationtime';
    } else if (productName.includes('person hour')) {
        return 'locationtime';
    } else if (productName.includes('crew hour')) {
        return 'locationtime';
    } else if (productName.includes('truck') && (productName.includes('travel'))) {
        return 'traveltime';
    }

    return undefined;
}
export function getAutoInfoFromCharge(charge: Charge) {
    return getAttributeValueByPrefix(charge.attributes, 'auto-info');

}

export function getAttributeValueByPrefix(
    attributes: string[],
    prefix: string,
    deliminator = '::',
) {
    if (!attributes) { return undefined; }
    const attr = attributes.find((_attr) => _attr.startsWith(prefix + deliminator));

    if (!attr) { return undefined; }

    const [_prefix, value] = attr.split(deliminator);
    return value;

}

export function /**
* Sums the estimatedTimeAtLocation property on a location
*/
    sumTimeAtEventLocations(
        locations: EventInfoLocationJobsV2[],
    ) {

    return locations.reduce((p, l) => l.estimatedTimeAtLocation, 0);
}

export function setDefaultTimeAtLocation(
    location: EventInfoLocationJobsV2,
    eventTypeInfo: EventTypeInfo,
    eventTimeFromConfig: TimeFromConfig,
): EventInfoLocationJobsV2 {
    // Priority:
    // 1. Config time (for start location)
    // 2. Event type default
    // 3. Existing estimated time

    let estimatedTime = location.estimatedTimeAtLocation;

    if (
        location.type === EventLocationTypes.start &&
        !isNil(eventTimeFromConfig?.[eventTypeInfo.value])
    ) {
        estimatedTime = eventTimeFromConfig[eventTypeInfo.value];
    } else if (
        !isNil(eventTypeInfo.defaultLocationTimes?.[location.type])
    ) {
        estimatedTime = eventTypeInfo.defaultLocationTimes[location.type];
    }

    return {
        ...location,
        estimatedTimeAtLocation: estimatedTime,
    };
}

function isBaseLocation( l: EventInfoLocationJobsV2) {
    return l?.type === EventLocationTypes.dockStart || l?.type === EventLocationTypes.dockEnd;
}

export function overrideEventTimeFromConfig(
    eventTypeInfo: EventTypeInfo,
    eventInfo: EventInfoJobsV2,
    eventTimeFromConfig: TimeFromConfig,
    virtualEstimateDefaultTime: number,
): EventInfoJobsV2 {
    const updatedEventInfo = cloneDeep(eventInfo);

    if (eventTypeInfo.value === 'estimating' && eventTimeFromConfig?.estimating) {
        updatedEventInfo.totalLocationTime = eventTimeFromConfig?.estimating;
        updatedEventInfo.totalTime = eventTimeFromConfig?.estimating;
    }

    if (eventTypeInfo.value === 'virtualEstimate' && eventTimeFromConfig?.virtualEstimate) {
        updatedEventInfo.totalLocationTime = eventTimeFromConfig?.virtualEstimate;
        updatedEventInfo.totalTime = eventTimeFromConfig?.virtualEstimate;
    }

    if (eventTypeInfo.value === 'virtualEstimate' && !eventTimeFromConfig?.virtualEstimate) {
        updatedEventInfo.totalLocationTime = virtualEstimateDefaultTime;
        updatedEventInfo.totalTime = virtualEstimateDefaultTime;
    }

    return updatedEventInfo;
}

export interface LocationTypeDetailsMap {
    basestart: BaseLocationFragment;
    baseend: BaseLocationFragment;
    start: BaseLocationFragment;
    end: BaseLocationFragment;
}

export function generateLocationTypeMap(job: JobDetailsV2Fragment): LocationTypeDetailsMap {
    const dock = getJobLocation(job, 'dock');
    return {
        basestart: dock,
        baseend: dock,
        start: getJobLocation(job, 'start'),
        end: getJobLocation(job, 'end'),
    };
}

export function getJobLocation(job: JobDetailsV2Fragment, type: string): BaseLocationFragment | undefined {
    return job?.locations?.find((l) => l.locationType === type)?.location;
}

function handleUserModifiedTravelTimes(
    finalEventInfo: EventInfoJobsV2,
    userModifiedTimes: UserModifiedTravelTime
): EventInfoJobsV2 {
    const updatedLocations = finalEventInfo.locations.map(location => {
        const userModifiedTime = userModifiedTimes[location.type as EventLocationTypes];
        if (!userModifiedTime) return location;

       // Calculate travel time offset, defaulting to the original offset if userModifiedTime is zero
       const travelTimeOffset = userModifiedTime.travelTimeToNextLocation !== undefined
       || userModifiedTime.travelTimeToNextLocation !== null
       ? userModifiedTime.travelTimeToNextLocation - (location.travelTimeToNextLocation || 0)
       : location.travelTimeToNextLocationOffset || 0;

        return {
            ...location,
            travelTimeToNextLocationOffset: travelTimeOffset,
            estimatedTimeAtLocation: userModifiedTime.estimatedTimeAtLocation ?? location.estimatedTimeAtLocation,
        };
   });

    const updatedTotals = calculateEventInfoTotals(updatedLocations);

    return {
        ...finalEventInfo,
        locations: updatedLocations,
        ...updatedTotals,
    };
}

export const calculateEventTime = (
    job: JobDetailsV2Fragment,
    eventId: string,
    dockToStart: boolean,
    endToDock: boolean,
    eventTimeFromConfig: TimeFromConfig,
    userModifiedTravelTimes: UserModifiedTravelTime,
) => {
    const event = job?.events?.find(e => e.id === eventId);
    if (!event) return undefined;

    const baseTime = precalculateEventInfo(event, job, dockToStart, endToDock);

    const finalTimeWithCharges = getDurationsFromCharges(
        baseTime,
        event.charges || [],
        eventTimeFromConfig,
    );

    // If user modifies travel time to next location or estimated time at location,
    // we need to overwrite the calculated time with the user modified time.
    if (userModifiedTravelTimes && Object.keys(userModifiedTravelTimes).length > 0) {
        return handleUserModifiedTravelTimes(finalTimeWithCharges, userModifiedTravelTimes);
    }

    return finalTimeWithCharges;
};

export function getNowAsTime(timezone: string): dayjs.Dayjs {
    let nowTime = dayjs().tz(timezone);
    const currentMinutes = nowTime.get('minutes');

    nowTime = nowTime.set('minutes', currentMinutes - (currentMinutes % 15)).set('seconds', 0);

    return nowTime;
}

export function getCurrentTimeWindows(timezone: string, possibleWindows) {
    const nowTime = getNowAsTime(timezone);
    return possibleWindows.filter(
        (window) => window.start < nowTime.unix() && window.end > nowTime.unix());
}

export function getStartUnix(time: number | Date | string, timezone: string): number {
    // Default, with restrictions ENABLED
    if (typeof time === 'number') {
      return time;
    } else if (typeof time === 'string'){
      return getNowAsTime(timezone).unix();
    }

    // Otherwise, we have restrictions DISABLED and we have to convert the calendar "time," which is
    // a Date() type

    // Convert formatted to dayJs and provide a timezone
    const dayJsStart = dayjs.tz(time, timezone);

    // return unix seconds
    return dayJsStart.unix();
}

/**
*
* @param eventInfo the event info
* @param initialStart the unix time to start the event
* @returns start and end in unix time, and the duration in seconds
*/
export function getTimingForQuery(
 eventInfo: EventInfoJobsV2,
 initialStart = 0,
) {
 let start = initialStart;

 let duration = 0;
 if (eventInfo?.eventType === 'virtualEstimate') {
   duration = eventInfo?.totalLocationTime;
 } else {

   const startIndex = eventInfo.locations.findIndex((v) => v.type === 'start');
   if (startIndex > 0) {
     // we have stops before the start, eg the dock
     const prelocations = eventInfo.locations.slice(0, startIndex);
     for (const loc of prelocations) {
       const offset = loc.estimatedTimeAtLocation + loc.travelTimeToNextLocation + loc.travelTimeToNextLocationOffset;
       start -= offset;
     }
   }

   duration = eventInfo.totalTime;
 }

 return {
   start,
   end: start + duration,
   duration,
 };
}

export function calculateDateRange(start: Date, end: Date) {

    // TODO: Taken from schedules.component.ts; optimise later

    const calendarStart = dayjs(start || new Date()).startOf('day');
    const calendarEnd = dayjs(end|| new Date()).endOf('day');

    const duration = 0;
    const min = dayjs(start || new Date()).startOf('day').subtract(duration, 'day').unix();
    const max = dayjs(end || new Date()).endOf('day').add(duration, 'day').unix();

    return {
        startDate: calendarStart.format('YYYY-MM-DD'),
        endDate: calendarEnd.format('YYYY-MM-DD'),
        min,
        max
    };
  };

export function calculateMinMaxFromTimestamp(timestamp: number) {
    const parsedDate = dayjs(timestamp * 1000);
    const min = parsedDate.startOf('day').unix();
    const max = parsedDate.endOf('day').unix();

    return {
        min,
        max,
    };
}

export function getCalendarDate(dateString: string): Date {
    const parsedDate = dayjs(dateString, 'MM/DD/YYYY');
    return parsedDate.toDate();
}
